/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { memo, useEffect, useMemo, useRef, type MutableRefObject } from 'react';

import { jsx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { componentWithFG } from '@atlaskit/platform-feature-flags-react';
import { Stack } from '@atlaskit/primitives/compiled';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import {
	RequestStatus,
	useBFFDataProvider,
	type ProductLink,
} from '@atlassian/navigation-bff-data-provider';
import {
	ERROR_LOCATION__GLOBAL_SHORTCUTS,
	useNavBoundaryContext,
} from '@atlassian/navigation-error-boundary';

import type {
	fnGetFilteredProductLinks,
	GlobalShortcutsProps,
	MappedGlobalShortcutsItem,
} from '../../../types';
import { DragAndDropOptionsProvider } from '../../drag-and-drop-options-provider';
import { MenuSkeletonItem } from '../../menu-skeleton-item';
import { ShortcutMenuItem } from '../../shortcut-menu-item';
import { ViewAllAppsItem } from '../../view-all-apps-item';

const MenuInnerMemoed = memo(MenuInnerRaw);

const MenuInner = componentWithFG(
	'navx-3138-gas-menu-rendering-perf-fix',
	MenuInnerMemoed,
	MenuInnerRaw,
);

export const MenuMemoed = memo(MenuRaw);

export function MenuRaw({
	shortcutItems = [],
	cloudId,
	orgId,
	userId,
	product,
	...restProps
}: GlobalShortcutsProps) {
	const { status, error, productLinks } = useBFFDataProvider({
		request: {},
		cloudId,
		orgId,
		userId,
		forceStatic: true,
		product,
	});

	const { handleError } = useNavBoundaryContext({
		location: ERROR_LOCATION__GLOBAL_SHORTCUTS,
		sentryConfig: { product },
		// Log backend errors only to Sentry
		// see: https://hello.atlassian.net/wiki/spaces/navx/pages/6439960649/NavX+-+FE+Observability#%3Acatchemall%3A--Error-handling
		disableAnalytics: true,
	});

	useEffect(() => {
		if (error) {
			handleError(error, {
				componentStack: 'Menu',
			});
		}
	}, [handleError, error]);

	return (
		<MenuInner
			shortcutItems={shortcutItems}
			status={status}
			productLinks={productLinks}
			{...restProps}
		/>
	);
}

function MenuInnerRaw({
	testId,
	enableDragAndDrop,
	viewAllAppsConfig,
	product,
	shortcutItems,
	status,
	productLinks,
	onUpdateGetFilteredProductLinks,
	openInSameTab,
}: GlobalShortcutsProps & {
	productLinks: Record<string, ProductLink[]>;
	status: RequestStatus | null;
}) {
	const onUpdateGetFilteredProductLinksRef = useRef<typeof onUpdateGetFilteredProductLinks>(
		onUpdateGetFilteredProductLinks,
	);
	onUpdateGetFilteredProductLinksRef.current = onUpdateGetFilteredProductLinks;

	/**
	 * Making a stable reference to `productLinks` so `getFilteredProductLinks` reads latest data
	 * without changing identity every render.
	 */
	const productLinksRef = useRef(productLinks);
	useEffect(() => {
		productLinksRef.current = productLinks;
	}, [productLinks]);

	useEffect(() => {
		onUpdateGetFilteredProductLinksRef.current?.((apsKey: string) =>
			getBffProductLinksForKey(apsKey, productLinksRef, status),
		);
	}, [productLinks, status]);

	const mappedShortcutItems = useMemo(
		() =>
			(shortcutItems ?? []).map((item, index) => {
				let key = (item.type === 'link' && item.apsKey) || `shortcut-${index}`;
				if (item.type === 'component' && fg('navx-3539-custom-shortcut-component-draggable')) {
					key = item?.apsKey ? item.apsKey : `shortcut-${index}`;
				}
				return { ...item, key };
			}),
		[shortcutItems],
	);

	const shortcutList = (
		<MenuList>
			{mappedShortcutItems?.map((item) =>
				createLinkItem(item, status, enableDragAndDrop, openInSameTab, product),
			)}
			{viewAllAppsConfig?.path && <ViewAllAppsItem {...viewAllAppsConfig} product={product} />}
		</MenuList>
	);

	return (
		<Stack testId={testId}>
			{enableDragAndDrop ? (
				<DragAndDropOptionsProvider enabled={enableDragAndDrop}>
					{shortcutList}
				</DragAndDropOptionsProvider>
			) : (
				shortcutList
			)}
		</Stack>
	);
}

function getBffProductLinksForKey(
	apsKey: string,
	productLinksRef: MutableRefObject<Record<string, ProductLink[]>>,
	status: RequestStatus | null,
): ReturnType<fnGetFilteredProductLinks> {
	return {
		links: productLinksRef.current[apsKey] ?? [],
		status,
	};
}

function createLinkItem(
	shortcut: MappedGlobalShortcutsItem,
	status: RequestStatus | null,
	enableDragAndDrop?: boolean,
	openInSameTab?: boolean,
	product?: string,
	useSkeleton?: boolean,
) {
	if (status?.toLowerCase() === RequestStatus.COMPLETE) {
		if (shortcut.type === 'component' && fg('navx-3539-custom-shortcut-component-draggable')) {
			return (
				<ShortcutMenuItem
					key={shortcut.key}
					shortcut={shortcut}
					dragAndDropEnabled={enableDragAndDrop}
					status={status}
					forceStatic={true}
					isInMoreMenu={false}
				/>
			);
		}

		return (
			<ShortcutMenuItem
				key={shortcut.key}
				shortcut={shortcut}
				openInSameTab={openInSameTab}
				status={status}
				forceStatic={true}
				expandItems={
					shortcut.type === 'expand' && (shortcut.items?.length ?? 0 > 1)
						? shortcut.items
						: undefined
				}
				promotedItem={shortcut.type === 'expand' ? shortcut.promoted : undefined}
				siteName={shortcut.type === 'expand' ? shortcut.siteName : undefined}
				product={product}
			/>
		);
	}

	// Show skeleton while BFF is loading
	if (useSkeleton && (status === RequestStatus.LOADING || status === RequestStatus.INIT)) {
		return <MenuSkeletonItem key={shortcut.key} />;
	}

	if (shortcut.type === 'expand' && shortcut.items) {
		return (
			<ShortcutMenuItem
				key={shortcut.key}
				shortcut={shortcut}
				expandItems={shortcut.items}
				forceStatic={true}
				product={product}
				openInSameTab={openInSameTab}
			/>
		);
	}

	if (shortcut.type === 'component' && fg('navx-3539-custom-shortcut-component-draggable')) {
		return (
			<ShortcutMenuItem
				key={shortcut.key}
				shortcut={shortcut}
				dragAndDropEnabled={enableDragAndDrop}
				status={status}
				forceStatic={true}
			/>
		);
	}

	return (
		<ShortcutMenuItem
			key={shortcut.key}
			shortcut={shortcut}
			status={status}
			forceStatic={true}
			product={product}
			openInSameTab={openInSameTab}
		/>
	);
}
